import { useCallback, useState } from "react";
import { UseFormWatch } from "react-hook-form";
import { useSearchContacts } from "api/queries/user/useSearchContacts";

interface usePreferredContactProps {
  preferredContact: any;
  watch: UseFormWatch<any>;
}

export default function usePreferredContact({ preferredContact, watch }: usePreferredContactProps) {
  const [contactSearchQuery, setContactSearchQuery] = useState("");
  const preferredContactsValue = watch("preferred_contact");

  // Create initial contacts array with preferred contact (if any)
  const initialContacts =
    preferredContact || preferredContactsValue
      ? [
          {
            label: preferredContact?.name || "",
            value: preferredContact?.id || preferredContactsValue,
            email: preferredContact?.email || "",
            phone: preferredContact?.phone_number || "",
          },
        ]
      : [];

  // Dynamic contacts search
  const { data: searchedContacts = initialContacts } = useSearchContacts(
    {
      query: contactSearchQuery,
      pageSize: 10,
      page: 1,
    },
    {
      select: (data) => {
        if (!data || !Array.isArray(data)) {
          return initialContacts;
        }

        const contacts = data.map((contact) => ({
          label: contact.full_name,
          value: contact.id,
          email: contact.email || "",
          phone: contact.phone_number || contact.phone || "",
        }));
        if (contacts.some((c) => c.value === preferredContactsValue)) {
          return contacts;
        }
        return [initialContacts?.[0], ...contacts].filter(Boolean);
      },
    },
  );

  // Callback for handling search input changes
  const handleContactSearch = useCallback((searchText) => {
    setContactSearchQuery(searchText);
  }, []);

  // Custom render function for contact options in dropdown
  const handleContactRenderOption = useCallback(
    ({ option }) => (
      <div className="p-2">
        <div className="font-weight-bold">{option.label}</div>
        {option.email && <div className="text-muted">{option.email}</div>}
      </div>
    ),
    [],
  );

  const selectedContactDetails = preferredContactsValue
    ? (() => {
        const contactId =
          typeof preferredContactsValue === "object" && preferredContactsValue
            ? preferredContactsValue.value
            : preferredContactsValue;

        if (!contactId || !Array.isArray(searchedContacts)) {
          return null;
        }

        const matchingContact = searchedContacts.find((contact) => contact && contact.value === contactId);

        return matchingContact
          ? {
              id: matchingContact.value,
              name: matchingContact.label,
              email: matchingContact.email || "",
              phone: matchingContact.phone || "",
            }
          : null;
      })()
    : null;

  return {
    handleContactSearch,
    handleContactRenderOption,
    contactSelectOptions: searchedContacts,
    selectedContactDetails,
  };
}
